import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSongs, useCreateSong, useUpdateSong, useDeleteSong, useFilterValues, songKeys } from '../hooks/useSongs';
import { useAuth } from '../AuthContext';

const SongsContext = createContext();

export const useSongsContext = () => {
  const context = useContext(SongsContext);
  if (!context) {
    throw new Error('useSongsContext must be used within SongsProvider');
  }
  return context;
};

export const SongsProvider = ({ children }) => {
  // Get authentication status
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  
  // Clear any stale cache on mount to fix potential cached empty results
  useEffect(() => {
    console.log('[SongsProvider] Mounted, clearing songs cache...');
    queryClient.removeQueries({ queryKey: ['songs'] });
  }, [queryClient]);
  
  // Local state for filters and pagination
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [filterGenre, setFilterGenre] = useState('all');
  const [filterRating, setFilterRating] = useState('all');
  const [filterMomento, setFilterMomento] = useState('all');
  const [filterEnergia, setFilterEnergia] = useState('all');
  const [filterDecade, setFilterDecade] = useState('all');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showOnlyDenonPendientes, setShowOnlyDenonPendientes] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('title');
  const [sortOrder, setSortOrder] = useState('desc'); // Default desc - asc tiene problemas en producción con muchas canciones
  
  // Debounce search text to reduce API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300); // Wait 300ms after user stops typing
    
    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);
  
  // Build filters object for API (using debounced search)
  const filters = useMemo(() => ({
    genre: filterGenre,
    rating: filterRating,
    momento: filterMomento,
    energia: filterEnergia,
    decade: filterDecade !== 'all' ? filterDecade : undefined,
    favorito: showFavoritesOnly ? true : undefined,
    pendiente_denon: showOnlyDenonPendientes ? true : undefined,
    search: debouncedSearchText, // Use debounced value for API calls
    sortBy: sortBy,
    sortOrder: sortOrder,
  }), [filterGenre, filterRating, filterMomento, filterEnergia, filterDecade, showFavoritesOnly, showOnlyDenonPendientes, debouncedSearchText, sortBy, sortOrder]);
  
  // React Query hooks - paginated data
  // Note: enabled is always true now - token check is done in queryFn
  const { data: paginatedData, isLoading, error, refetch } = useSongs(
    currentPage,
    itemsPerPage,
    filters,
    true // Always enabled
  );
  
  // Load all unique filter values from backend
  const { data: filterValuesData } = useFilterValues(true); // Always enabled
  
  // Extract songs and pagination from response
  const songs = paginatedData?.data || [];
  const pagination = paginatedData?.pagination || { total: 0, total_pages: 0 };
  
  const createSongMutation = useCreateSong();
  const updateSongMutation = useUpdateSong();
  const deleteSongMutation = useDeleteSong();

  // Filters are now applied in backend, so filteredSongs = songs
  // Keep for backward compatibility but they're the same
  const filteredSongs = songs;
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterGenre, filterRating, filterMomento, filterEnergia, showFavoritesOnly, showOnlyDenonPendientes, searchText]);

  // Get available genres from backend (all genres in library, not just current page)
  const availableGenres = useMemo(() => {
    return filterValuesData?.genres || [];
  }, [filterValuesData]);
  
  // Get available momentos from backend
  const availableMomentos = useMemo(() => {
    return filterValuesData?.momentos || [];
  }, [filterValuesData]);
  
  // Get available energia values from backend
  const availableEnergia = useMemo(() => {
    return filterValuesData?.energia || [];
  }, [filterValuesData]);
  
  // Get available ratings from backend
  const availableRatings = useMemo(() => {
    return filterValuesData?.ratings || [];
  }, [filterValuesData]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setSearchText('');
    setFilterGenre('all');
    setFilterRating('all');
    setFilterMomento('all');
    setFilterEnergia('all');
    setFilterDecade('all');
    setShowFavoritesOnly(false);
    setShowOnlyDenonPendientes(false);
    setSortBy('title');
    setSortOrder('desc');
  }, []);

  // Check if any filters are active (NOT including sorting - sorting is not a filter)
  const hasActiveFilters = useMemo(() => {
    return !!(
      searchText ||
      (filterGenre && filterGenre !== 'all') ||
      (filterRating && filterRating !== 'all') ||
      (filterMomento && filterMomento !== 'all') ||
      (filterEnergia && filterEnergia !== 'all') ||
      (filterDecade && filterDecade !== 'all') ||
      showFavoritesOnly ||
      showOnlyDenonPendientes
    );
  }, [searchText, filterGenre, filterRating, filterMomento, filterEnergia, filterDecade, showFavoritesOnly, showOnlyDenonPendientes]);

  const value = {
    // Data
    songs,
    filteredSongs,
    availableGenres,
    availableMomentos,
    availableEnergia,
    availableRatings,
    isLoading,
    error,

    // Pagination
    currentPage,
    setCurrentPage,
    itemsPerPage,
    totalPages: pagination.total_pages,
    totalSongs: pagination.total,
    hasNextPage: pagination.has_next,
    hasPrevPage: pagination.has_prev,

    // Sorting
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,

    // Filters
    searchText,
    setSearchText,
    filterGenre,
    setFilterGenre,
    filterRating,
    setFilterRating,
    filterMomento,
    setFilterMomento,
    filterEnergia,
    setFilterEnergia,
    filterDecade,
    setFilterDecade,
    showFavoritesOnly,
    setShowFavoritesOnly,
    showOnlyDenonPendientes,
    setShowOnlyDenonPendientes,
    hasActiveFilters,
    clearAllFilters,

    // Actions
    refetchSongs: refetch,
    forceRefreshSongs: useCallback(() => {
      console.log('[SongsContext] Force refreshing songs - invalidating cache...');
      queryClient.invalidateQueries({ queryKey: songKeys.all });
    }, [queryClient]),
    createSong: createSongMutation.mutateAsync,
    updateSong: updateSongMutation.mutateAsync,
    deleteSong: deleteSongMutation.mutateAsync,

    // Mutations loading states
    isCreating: createSongMutation.isPending,
    isUpdating: updateSongMutation.isPending,
    isDeleting: deleteSongMutation.isPending,
  };

  return <SongsContext.Provider value={value}>{children}</SongsContext.Provider>;
};
